import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Account, Segment } from '../../interfaces/accounting.interface';
import {
    InterSegmentEvent, InterSegmentInterestPreview, InterSegmentLoan,
} from '../../interfaces/interSegment.interface';
import { InterSegmentService } from '../../services/interSegment.service';
import { SegmentService } from '../../services/segment.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import SearchableSelect from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { leafAccounts } from '../../utils/postingAccounts';

type Mode = 'advance' | 'repay' | 'interest';

interface CreateDraft { lender: string; borrower: string; rate: string; basis: number; start: string; notes: string }
const emptyCreate = (): CreateDraft => ({ lender: '', borrower: '', rate: '0', basis: 365, start: toLocalIsoDate(), notes: '' });

/**
 * Inter-segment loans: one segment lends cash to another. Advances, interest and repayments each post a
 * journal (lender due-from / borrower due-to, with the counterparty segment on the line); interest accrues on
 * the daily outstanding principal and repayments clear interest first.
 */
const InterSegmentLoans: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.SEGMENT_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [loans, setLoans] = useState<InterSegmentLoan[]>([]);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);
    const [create, setCreate] = useState<CreateDraft | null>(null);
    const [detail, setDetail] = useState<InterSegmentLoan | null>(null);
    const [mode, setMode] = useState<Mode | null>(null);
    const [date, setDate] = useState(toLocalIsoDate());
    const [amount, setAmount] = useState('');
    const [cash, setCash] = useState('');
    const [preview, setPreview] = useState<InterSegmentInterestPreview | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setLoans(await InterSegmentService.listLoans(institutionId));
        } catch (e) {
            toast.showApiError(e, 'Failed to load inter-segment loans');
        } finally {
            setLoading(false);
        }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        load();
        SegmentService.getByInstitution().then(p => setSegments(p.content ?? [])).catch(() => setSegments([]));
        AccountService.getAllAccounts().then(a => setAccounts(leafAccounts(a ?? []).filter(x => x.accountType === 'ASSET')))
            .catch(() => setAccounts([]));
    }, [load]);

    const visible = useMemo(() => loans.filter(l => !status || l.status === status), [loans, status]);
    const segName = (id: string) => segments.find(s => s.id === id)?.name ?? id;

    const openDetail = async (loan: InterSegmentLoan) => {
        try {
            setDetail(await InterSegmentService.getLoan(loan.id));
        } catch (e) {
            toast.showApiError(e, 'Failed to open the loan');
        }
    };

    const refresh = async (updated: InterSegmentLoan) => {
        setDetail(updated);
        await load();
    };

    const createLoan = async () => {
        if (!create) return;
        setBusy(true);
        try {
            const loan = await InterSegmentService.createLoan({
                institutionId, lenderSegmentId: create.lender, borrowerSegmentId: create.borrower,
                annualRatePct: Number(create.rate), dayCountBasis: create.basis, startDate: create.start,
                notes: create.notes.trim() || undefined,
            });
            toast.showSuccess(`Loan ${loan.loanNumber} created`);
            setCreate(null);
            await load();
            setDetail(loan);
        } catch (e) {
            toast.showApiError(e, 'Failed to create the loan');
        } finally {
            setBusy(false);
        }
    };

    const startMode = (m: Mode) => {
        setMode(m);
        setDate(toLocalIsoDate());
        setAmount('');
        setCash('');
        setPreview(null);
    };

    const loadPreview = async () => {
        if (!detail) return;
        try {
            setPreview(await InterSegmentService.previewInterest(detail.id, date));
        } catch (e) {
            setPreview(null);
            toast.showApiError(e, 'Failed to compute interest');
        }
    };

    const submitMode = async () => {
        if (busy || !detail || !mode) return;
        setBusy(true);
        try {
            let updated: InterSegmentLoan;
            if (mode === 'interest') {
                updated = await InterSegmentService.postInterest(detail.id, date);
            } else {
                const dto = { date, amount: Number(amount), cashAccountId: cash || undefined };
                updated = mode === 'advance'
                    ? await InterSegmentService.advance(detail.id, dto)
                    : await InterSegmentService.repay(detail.id, dto);
            }
            toast.showSuccess('Posted');
            setMode(null);
            await refresh(updated);
        } catch (e) {
            toast.showApiError(e, 'Failed to post');
        } finally {
            setBusy(false);
        }
    };

    const reverse = async (ev: InterSegmentEvent) => {
        if (busy || !detail || !window.confirm(`Reverse this ${humanizeEnum(ev.eventType).toLowerCase()} of ${formatMoney(ev.amount)}?`)) return;
        setBusy(true);
        try {
            await refresh(await InterSegmentService.reverseEvent(detail.id, ev.id));
            toast.showSuccess('Reversed');
        } catch (e) {
            toast.showApiError(e, 'Failed to reverse');
        } finally {
            setBusy(false);
        }
    };

    const close = async () => {
        if (busy || !detail) return;
        setBusy(true);
        try {
            await refresh(await InterSegmentService.closeLoan(detail.id));
        } catch (e) {
            toast.showApiError(e, 'Failed to close the loan');
        } finally {
            setBusy(false);
        }
    };

    const createValid = !!create && !!create.lender && !!create.borrower && create.lender !== create.borrower
        && Number(create.rate) >= 0 && !!create.start;
    const modeValid = mode === 'interest' ? !!date : Number(amount) > 0 && !!date;

    return (
        <Box data-help-page="accounting.interSegLoans.page" sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5">Inter-segment loans</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button data-help="accounting.interSegLoans.reconLink" component={RouterLink} to="/accounting/inter-segment/reconciliation" variant="outlined">Reconciliation</Button>
                    <Button data-help="accounting.interSegLoans.settingsLink" component={RouterLink} to="/accounting/inter-segment/settings" variant="outlined">Settings</Button>
                    {canManage && <Button data-help="accounting.interSegLoans.new" variant="contained" onClick={() => setCreate(emptyCreate())}>New loan</Button>}
                </Box>
            </Box>

            <Box data-help-section="accounting.interSegLoans.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TextField data-help="accounting.interSegLoans.status" select label="Status" size="small" value={status} onChange={e => setStatus(e.target.value)}
                    sx={{ flex: '1 1 160px', maxWidth: 220 }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="OPEN">Open</MenuItem>
                    <MenuItem value="CLOSED">Closed</MenuItem>
                </TextField>
            </Box>

            <TableContainer data-help-section="accounting.interSegLoans.table" component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.interSegLoans.col.loan">Loan</TableCell><TableCell data-help="accounting.interSegLoans.col.lender">Lender</TableCell><TableCell data-help="accounting.interSegLoans.col.borrower">Borrower</TableCell>
                            <TableCell data-help="accounting.interSegLoans.col.rate" align="right">Rate %</TableCell><TableCell data-help="accounting.interSegLoans.col.principal" align="right">Principal</TableCell>
                            <TableCell data-help="accounting.interSegLoans.col.interestDue" align="right">Interest due</TableCell><TableCell data-help="accounting.interSegLoans.col.total" align="right">Total</TableCell>
                            <TableCell data-help="accounting.interSegLoans.col.status">Status</TableCell><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.map(l => (
                            <TableRow key={l.id} hover>
                                <TableCell>{l.loanNumber}</TableCell>
                                <TableCell>{l.lenderSegmentName ?? segName(l.lenderSegmentId)}</TableCell>
                                <TableCell>{l.borrowerSegmentName ?? segName(l.borrowerSegmentId)}</TableCell>
                                <TableCell align="right">{Number(l.annualRatePct).toFixed(2)}</TableCell>
                                <TableCell align="right">{formatMoney(l.principalOutstanding)}</TableCell>
                                <TableCell align="right">{formatMoney(l.interestOutstanding)}</TableCell>
                                <TableCell align="right">{formatMoney(l.totalOutstanding)}</TableCell>
                                <TableCell><Chip size="small" label={humanizeEnum(l.status)} color={l.status === 'OPEN' ? 'primary' : 'default'} /></TableCell>
                                <TableCell><Button data-help="accounting.interSegLoans.action.open" size="small" onClick={() => openDetail(l)}>Open</Button></TableCell>
                            </TableRow>
                        ))}
                        {!loading && visible.length === 0 && (
                            <TableRow><TableCell colSpan={9} align="center">No inter-segment loans yet.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* New loan */}
            <Dialog data-help-page="accounting.interSegLoans.create.page" open={!!create} onClose={() => setCreate(null)} fullWidth maxWidth="sm">
                <DialogTitle>New inter-segment loan</DialogTitle>
                {create && (
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <SearchableSelect data-help="accounting.interSegLoans.create.field.lender" options={segments} value={create.lender} label="Lender segment"
                            onChange={id => setCreate({ ...create, lender: id })}
                            getOptionLabel={s => `${s.code} — ${s.name}`} />
                        <SearchableSelect data-help="accounting.interSegLoans.create.field.borrower" options={segments.filter(s => s.id !== create.lender)} value={create.borrower}
                            label="Borrower segment" onChange={id => setCreate({ ...create, borrower: id })}
                            getOptionLabel={s => `${s.code} — ${s.name}`} />
                        <TextField data-help="accounting.interSegLoans.create.field.rate" label="Annual interest rate %" type="number" value={create.rate}
                            onChange={e => setCreate({ ...create, rate: e.target.value })} inputProps={{ min: 0, max: 100, step: '0.01' }} />
                        <TextField data-help="accounting.interSegLoans.create.field.basis" select label="Day-count basis" value={create.basis}
                            onChange={e => setCreate({ ...create, basis: Number(e.target.value) })}>
                            <MenuItem value={365}>Actual / 365</MenuItem>
                            <MenuItem value={360}>Actual / 360</MenuItem>
                        </TextField>
                        <TextField data-help="accounting.interSegLoans.create.field.start" label="Start date" type="date" value={create.start} InputLabelProps={{ shrink: true }}
                            onChange={e => setCreate({ ...create, start: e.target.value })} />
                        <TextField data-help="accounting.interSegLoans.create.field.notes" label="Notes" value={create.notes} multiline minRows={2}
                            onChange={e => setCreate({ ...create, notes: e.target.value })} />
                    </DialogContent>
                )}
                <DialogActions>
                    <Button data-help="accounting.interSegLoans.create.cancel" onClick={() => setCreate(null)}>Cancel</Button>
                    <Button data-help="accounting.interSegLoans.create.submit" variant="contained" onClick={createLoan} disabled={!createValid || busy}>Create</Button>
                </DialogActions>
            </Dialog>

            {/* Loan detail */}
            <Dialog data-help-page="accounting.interSegLoans.detail.page" open={!!detail} onClose={() => { setDetail(null); setMode(null); }} fullWidth maxWidth="md">
                <DialogTitle>
                    {detail?.loanNumber} — {detail?.lenderSegmentName} to {detail?.borrowerSegmentName}
                </DialogTitle>
                {detail && (
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            <Typography variant="body2">Principal: <b>{formatMoney(detail.principalOutstanding)}</b></Typography>
                            <Typography variant="body2">Interest due: <b>{formatMoney(detail.interestOutstanding)}</b></Typography>
                            <Typography variant="body2">Total: <b>{formatMoney(detail.totalOutstanding)}</b></Typography>
                            <Typography variant="body2">Rate: {Number(detail.annualRatePct).toFixed(2)}% (A/{detail.dayCountBasis})</Typography>
                            <Typography variant="body2">
                                Interest posted through: {detail.interestPostedThrough ? formatDate(detail.interestPostedThrough) : 'not yet'}
                            </Typography>
                        </Box>
                        {canManage && detail.status === 'OPEN' && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Button data-help="accounting.interSegLoans.action.advance" variant="outlined" onClick={() => startMode('advance')} disabled={busy}>Advance cash</Button>
                                <Button data-help="accounting.interSegLoans.action.repay" variant="outlined" onClick={() => startMode('repay')} disabled={busy}>Record repayment</Button>
                                <Button data-help="accounting.interSegLoans.action.interest" variant="outlined" onClick={() => startMode('interest')} disabled={busy}>Post interest</Button>
                                <Button data-help="accounting.interSegLoans.action.close" variant="outlined" color="inherit" onClick={close}
                                    disabled={busy || Number(detail.totalOutstanding) !== 0}>Close loan</Button>
                            </Box>
                        )}
                        {mode && (
                            <Paper data-help-section="accounting.interSegLoans.detail.modePanel" variant="outlined" sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                                <TextField data-help="accounting.interSegLoans.detail.field.date" label={mode === 'interest' ? 'Interest up to' : 'Date'} type="date" value={date}
                                    InputLabelProps={{ shrink: true }} onChange={e => { setDate(e.target.value); setPreview(null); }}
                                    sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                                {mode !== 'interest' && (
                                    <>
                                        <TextField data-help="accounting.interSegLoans.detail.field.amount" label="Amount" type="number" value={amount} onChange={e => setAmount(e.target.value)}
                                            inputProps={{ min: 0, step: '0.01' }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                                        <Box sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                                            <AccountAutocomplete data-help="accounting.interSegLoans.detail.field.cash" accounts={accounts} value={cash} onChange={setCash}
                                                label="Cash / bank account" placeholder="Settings default" />
                                        </Box>
                                    </>
                                )}
                                {mode === 'interest' && (
                                    <Box sx={{ flex: '1 1 260px' }}>
                                        <Button data-help="accounting.interSegLoans.action.preview" size="small" onClick={loadPreview}>Preview</Button>
                                        {preview && (
                                            <Typography variant="body2">
                                                {formatDate(preview.periodFrom)} to {formatDate(preview.periodTo)} ({preview.days} days):
                                                {' '}<b>{formatMoney(preview.interest)}</b>
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button data-help="accounting.interSegLoans.action.post" variant="contained" onClick={submitMode} disabled={!modeValid || busy}>Post</Button>
                                    <Button data-help="accounting.interSegLoans.detail.modeCancel" onClick={() => setMode(null)}>Cancel</Button>
                                </Box>
                            </Paper>
                        )}
                        <TableContainer data-help-section="accounting.interSegLoans.events" component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell data-help="accounting.interSegLoans.event.col.date">Date</TableCell><TableCell data-help="accounting.interSegLoans.event.col.event">Event</TableCell>
                                        <TableCell data-help="accounting.interSegLoans.event.col.amount" align="right">Amount</TableCell><TableCell data-help="accounting.interSegLoans.event.col.principal" align="right">Principal</TableCell>
                                        <TableCell data-help="accounting.interSegLoans.event.col.interest" align="right">Interest</TableCell><TableCell data-help="accounting.interSegLoans.event.col.journal">Journal</TableCell><TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(detail.events ?? []).map(ev => (
                                        <TableRow key={ev.id} sx={ev.reversed ? { opacity: 0.55 } : undefined}>
                                            <TableCell>{formatDate(ev.eventDate)}</TableCell>
                                            <TableCell>
                                                {humanizeEnum(ev.eventType)}
                                                {ev.periodFrom && ev.periodTo ? ` (${formatDate(ev.periodFrom)} – ${formatDate(ev.periodTo)})` : ''}
                                                {ev.reversed && <Chip size="small" label="Reversed" sx={{ ml: 1 }} />}
                                            </TableCell>
                                            <TableCell align="right">{formatMoney(ev.amount)}</TableCell>
                                            <TableCell align="right">{formatMoney(ev.principalAmount)}</TableCell>
                                            <TableCell align="right">{formatMoney(ev.interestAmount)}</TableCell>
                                            <TableCell>
                                                {ev.journalEntryId && (
                                                    <RouterLink data-help="accounting.interSegLoans.action.viewJournal" to={`/accounting/journal-entries/${ev.journalEntryId}`}>View</RouterLink>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {canManage && !ev.reversed && (
                                                    <Button data-help="accounting.interSegLoans.action.reverse" size="small" color="warning" onClick={() => reverse(ev)} disabled={busy}>Reverse</Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(detail.events ?? []).length === 0 && (
                                        <TableRow><TableCell colSpan={7} align="center">No events yet. Record the first advance.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {detail.notes && <Alert severity="info">{detail.notes}</Alert>}
                    </DialogContent>
                )}
                <DialogActions><Button data-help="accounting.interSegLoans.detail.close" onClick={() => { setDetail(null); setMode(null); }}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export default InterSegmentLoans;
